/**
  Add a new partner component
 */
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import InputContainer from './input.component';
import { RequiredAsterisk, SelectOne } from './utils.component';
import { PartnerFormModel } from '../models/partner.model';
import { HolderModel } from '../models/holder.model';
import { canadaProvinceList, usaArmedForces, usaStateList } from '../util/form-values';
import { validatePartner, validatePartnerEmail } from '../util/validation-helpers';
import styles from './partner.module.scss';

type PartnerFullFormProps = {
  holder: HolderModel;
  partner: PartnerFormModel;
  handleFormSubmit: () => Promise<void>;
  handleInputChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

const PartnerFullForm = ({
  holder,
  partner,
  handleFormSubmit,
  handleInputChange,
}: PartnerFullFormProps) => {
  const [showFormErrors, setShowFormErrors] = useState<boolean>(false);

  const onFormSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void> =
    async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      const hasFormError = Object.values(validatePartner(partner, holder)).some(Boolean);
      if (hasFormError) {
        setShowFormErrors(true);
        return
      }
      handleFormSubmit();
    };

  const isUnitedStates = partner.country === 'USA';
  const validationErrors: any = showFormErrors ? validatePartner(partner, holder) : {};

  return (
    <div>
      <label className={styles.title}>Add A Season Ticket Partner</label>
      <p>An existing account was not found with that email address. Use the form below to add them as a partner.</p>
      <form className={styles.wrapper} onSubmit={onFormSubmit}>
        <div className={styles.container2}>
          <div className={styles.flexRow}>
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.firstName}
              errorMessage={`Please enter partner's first name.`}
              label='First Name'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                id: 'firstName',
                name: 'firstName',
                value: partner.firstName,
                onChange: handleInputChange,
                required: true,
                maxLength: 80,
                autoFocus: true,
              }}
            />
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.lastName}
              errorMessage={`Please enter partner's last name.`}
              label='Last Name'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                id: 'lastName',
                name: 'lastName',
                value: partner.lastName,
                onChange: handleInputChange,
                required: true,
                maxLength: 80,
              }}
            />
          </div>
          <div className={styles.flexRow}>
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.address1}
              errorMessage={`Please enter partner's address.`}
              label='Address'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'address1',
                id: 'address1',
                value: partner.address1,
                onChange: handleInputChange,
                required: true,
                maxLength: 100,
              }}
            />
            <InputContainer
              className={styles.rowContainer}
              label='Address 2'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'address2',
                id: 'address2',
                value: partner.address2,
                onChange: handleInputChange,
                maxLength: 100,
              }}
            />
          </div>
          <div className={styles.flexRow}>
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.city}
              errorMessage={`Please enter partner's city.`}
              label='City'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'city',
                id: 'city',
                value: partner.city,
                onChange: handleInputChange,
                required: true,
                maxLength: 50,
              }}
            />
            <div className={styles.selectRow}>
              <label className={styles.label} htmlFor='country'><RequiredAsterisk /> Country</label>
              <select className={styles.selectItem} name='country' id='country' value={partner.country} onChange={handleInputChange}>
                <option value='USA'>United States</option>
                <option value='CAN'>Canada</option>
              </select>
            </div>
          </div>
          <div className={styles.flexRow}>
            <div className={styles.selectRow}>
              <label className={styles.label} htmlFor='state'><RequiredAsterisk /> {isUnitedStates ? 'State' : 'Province'}</label>
              <select className={styles.selectItem} name='state' id='state' value={partner.state} onChange={handleInputChange} required>
                <SelectOne />
                {isUnitedStates ? (
                  <>
                    <optgroup label='States'>
                      {usaStateList.map(({ abbr, name }) => (
                        <option key={abbr} value={abbr}>{abbr} - {name}</option>
                      ))}
                    </optgroup>
                    <optgroup label='US Armed Forces'>
                      {usaArmedForces.map(({ abbr, name }) => (
                        <option key={`${abbr}-${name}`} value={abbr}>{abbr} - {name}</option>
                      ))}
                    </optgroup>
                  </>
                ) : (
                    <optgroup label='Provinces'>
                      {canadaProvinceList.map(({ abbr, name }) => (
                        <option key={abbr} value={abbr}>{abbr} - {name}</option>
                      ))}
                    </optgroup>
                  )}
              </select>
            </div>
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.zipCode}
              errorMessage='Please enter a valid zip/postal code.'
              label='Zip/Postal Code'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'zipCode',
                id: 'zipCode',
                value: partner.zipCode,
                onChange: handleInputChange,
                required: true,
                maxLength: isUnitedStates ? 5 : 7,
              }}
            />
          </div>
          <div className={styles.flexRow}>
            <InputContainer
              className={styles.rowContainer}
              label='Email Address'
              labelProps={{ className: styles.label }}
              inputProps={{
                type: 'email',
                className: styles.textBox,
                name: 'email',
                id: 'email',
                value: partner.email,
                required: true,
                maxLength: 80,
                disabled: true,
              }}
            />
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.phoneNumber}
              errorMessage={`Please enter partner's phone number.`}
              label='Phone'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'phoneNumber',
                id: 'phoneNumber',
                value: partner.phoneNumber,
                onChange: handleInputChange,
                type: 'tel',
                required: true,
                maxLength: 10,
                placeholder: '1234567890',
              }}
            />
          </div>
          <div className={styles.flexRow}>
            <div className={styles.selectRow}>
              <label className={styles.label} htmlFor='partnerType'><RequiredAsterisk /> Partner Type</label>
              <select id='partnerType' name='partnerType' className={styles.selectItem} onChange={handleInputChange} value={partner.partnerType} required>
                <SelectOne />
                <option value='Client or business associate' disabled>Client or business associate</option>
                <option value='Colleague at work' disabled>Colleague at work</option>
                <option value='Friend'>Friend</option>
                <option value='Other' disabled>Other</option>
                <option value='Relative' disabled>Relative</option>
              </select>
            </div>
            <InputContainer
              className={styles.rowContainer}
              label='Company Name'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'company',
                id: 'company',
                value: partner.company,
                onChange: handleInputChange,
                maxLength: 100,
              }}
            />
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <button className={styles.submitBtn} type='submit'>Submit</button>
        </div>
      </form>
    </div>
  );

};

type PartnerEmailProps = {
  holder: HolderModel;
  email: string;
  handleFormSubmit: () => Promise<void>;
  handleInputChange?: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

const PartnerEmail = ({
  holder,
  email,
  handleFormSubmit,
  handleInputChange,
}: PartnerEmailProps) => {

  const [showFormErrors, setShowFormErrors] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null!);

  useEffect(() => {
    const emailInput = formRef.current.querySelector<HTMLInputElement>('#email');
    if (emailInput) {
      if (emailInput.value === '') {
        emailInput.focus();
      }
    }
  }, [ email ]);

  const onFormSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void> =
    async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();

      const hasFormError = Object.values(validatePartnerEmail(email, holder)).some(Boolean);
      if (hasFormError) {
        setShowFormErrors(true);
        return
      }
      setShowFormErrors(false);
      handleFormSubmit();
    };

  const validationErrors: any = showFormErrors
    ? validatePartnerEmail(email, holder)
    : {};

  return (
    <div>
      <label className={styles.title}>Add A Season Ticket Partner</label>
      <p>{new Date().getFullYear()} Sweet Spot Partner ID Cards will be accessible via the MLB Ballpark app. Instructions to activate the ID card will be emailed to the address you enter below.</p>
      <form className={styles.wrapper} onSubmit={onFormSubmit} ref={formRef}>
        <InputContainer
          className={classnames(styles.rowContainer, styles.emailOnly)}
          hasError={validationErrors.email || validationErrors.emailSame}
          errorMessage={validationErrors.email ? 'Please enter a valid email address.' : validationErrors.emailSame ? `Partner email address can not be the same as planholder's.`: ''}
          label='Email Address'
          labelProps={{ className: styles.label }}
          inputProps={{
            type: 'email',
            className: styles.textBox,
            name: 'email',
            id: 'email',
            value: email,
            onChange: handleInputChange,
            required: true,
            maxLength: 80,
          }}
        />
        <div className={styles.buttonContainer}>
          <button className={styles.submitBtn} type='submit'>Submit</button>
        </div>
      </form>
    </div>
  );
};

export { PartnerFullForm, PartnerEmail };
